import { t } from "@repo/shared/i18n";
import { isSafari } from "react-device-detect";

import { User } from "../redux/user/userSlice";
import { isChromeVersionUpToDate } from "./isChromeVersionUpToDate";

type Props = {
  user?: User;
  doneRequest?: boolean;
  startRequest?: boolean;
  /** Abandon a captured import; the extension forgets it (extension >= 2.4.8). */
  cancelRequest?: boolean;
  /** Start or resume Synchronize All Collected Players (extension >= 2.4.18). */
  collectedPlayersSyncRequest?: boolean;
  safariMobile?: boolean;
};

export interface ExtensionResponse {
  version?: string;
  msg?: string;
  hasEaAccess?: boolean;
  /**
   * The club is already claimed by another EasySBC account, so the backend
   * rejected the import (ADR 0012). Nothing was written and no players were
   * deleted — callers must not show a success state. Absent on extensions
   * that predate the claim.
   */
  clubClaimBlocked?: boolean;
}

export const sendMessageToExtension = async ({
  user,
  doneRequest = false,
  startRequest = false,
  cancelRequest = false,
  collectedPlayersSyncRequest = false,
  safariMobile = isSafari && !!localStorage.getItem("FROM_EXTENSION_APP"),
}: Props): Promise<ExtensionResponse> => {
  // Logged out, no Authorization at all: the extension drops any stored token
  // on a Start without one, so a logged-out capture never syncs into whoever
  // used this browser before.
  const accessToken = JSON.parse(
    localStorage.getItem("access_token") ?? "null",
  );
  let data: any = {
    dev: import.meta.env.VITE_APP_EXTENSION_ENV === "dev",
    staging: import.meta.env.VITE_APP_EXTENSION_ENV === "staging",
    feEnv: import.meta.env.VITE_APP_ENVIRONMENT,
    allowTracking: !!user?.allowTracking,
    showPopover: true,
    ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
  };
  if (doneRequest) {
    data = { doneRequest: true, ...data };
  } else if (cancelRequest) {
    data = { cancelRequest: true, ...data };
  } else if (startRequest) {
    data = { startRequest: true, userRequest: true, ...data };
  } else if (collectedPlayersSyncRequest) {
    data = { collectedPlayersSyncRequest: true, userRequest: true, ...data };
  } else {
    data = { userRequest: true, ...data };
  }
  const runtime = safariMobile
    ? window.browser?.runtime
    : window.chrome?.runtime;

  const extenstionId = safariMobile
    ? import.meta.env.VITE_APP_SAFARI_EXTENSION_ID
    : import.meta.env.VITE_APP_EXTENSION_ID;

  if (!runtime) throw new Error(t("clubImport.extensionNotInstalled"));

  const res = await new Promise<ExtensionResponse>((r) => {
    runtime?.sendMessage(extenstionId!, data, (res: ExtensionResponse) => {
      r(res);
    });
  });

  if (!res) throw new Error(t("clubImport.extensionNotInstalled"));

  const version = res?.version;

  if (version) {
    if (!isChromeVersionUpToDate([2, 2, 20], version)) {
      throw new Error(t("clubImport.updateExtension"));
    }
  }

  return res;
};
